'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { User } from '@supabase/supabase-js';

import { getSiteUrl } from '@/lib/app-url';
import {
  buildOAuthApiCallbackUrl,
  hasPkceCodeVerifierCookie,
  resolveOAuthLocaleFromPathname,
  writeOAuthHandoffCookies,
} from '@/lib/auth/oauth-callback-url';
import { normalizeUserRole, type RawUserRole } from '@/lib/auth/roles';
import type { UserProfile } from '@/types';

interface AuthError {
  message: string;
  status?: number;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: AuthError | null; profile?: UserProfile | null }>;
  signInWithPhone: (
    phone: string,
  ) => Promise<{ error: AuthError | null; assessmentId?: string | null }>;
  sendEmailOtp: (params: {
    email: string;
    fullName?: string;
    role?: 'user' | 'customer' | 'provider' | 'admin';
    shouldCreateUser?: boolean;
  }) => Promise<{ error: AuthError | null }>;
  signInWithOAuth: (provider: 'google') => Promise<{ error: AuthError | null }>;
  verifyOtp: (
    phone: string,
    token: string,
    assessmentId?: string | null,
  ) => Promise<{ error: AuthError | null }>;
  verifyEmailOtp: (email: string, token: string) => Promise<{ error: AuthError | null }>;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    role: 'user' | 'provider',
  ) => Promise<{ error: AuthError | null }>;
  signUpWithPhone: (
    phone: string,
    fullName: string,
    role: 'user' | 'provider',
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: AuthError | null }>;
  refreshSession: () => Promise<void>;
}

interface ServerSessionResponse {
  user: User | null;
  profile: UserProfile | null;
}

async function getBrowserSupabase() {
  const { getSupabaseBrowserClient } = await import('@/lib/supabase');
  return getSupabaseBrowserClient();
}

function buildOAuthCallbackUrl(): string {
  // Exact allow-list URL (no ?next=). Locale/next live in handoff cookies.
  // Add https://<host>/api/auth/callback to Supabase Auth → Redirect URLs.
  const origin = typeof window === 'undefined' ? getSiteUrl() : window.location.origin;

  if (typeof window !== 'undefined') {
    const locale = resolveOAuthLocaleFromPathname(window.location.pathname);
    writeOAuthHandoffCookies(locale);
  }

  return buildOAuthApiCallbackUrl(origin);
}

async function clearServerSession() {
  await fetch('/api/auth/session', { method: 'DELETE', credentials: 'same-origin' });
}

function normalizeProfile(profile: Partial<UserProfile> | null | undefined): UserProfile | null {
  if (!profile?.id) {
    return null;
  }

  return {
    id: profile.id,
    email: profile.email ?? '',
    full_name: profile.full_name ?? '',
    phone: profile.phone ?? undefined,
    avatar_url: profile.avatar_url ?? undefined,
    role: normalizeUserRole((profile.role ?? null) as RawUserRole),
    created_at: profile.created_at ?? new Date().toISOString(),
    updated_at: profile.updated_at ?? new Date().toISOString(),
    is_verified: profile.is_verified ?? false,
    last_login: profile.last_login,
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  // Bumped on optimistic sign-in so in-flight /api/auth/me responses cannot wipe the session.
  const sessionEpochRef = useRef(0);

  const fetchServerSession = useCallback(async (): Promise<ServerSessionResponse | null> => {
    const epochAtStart = sessionEpochRef.current;
    try {
      const response = await fetch('/api/auth/me', {
        cache: 'no-store',
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error('Failed to resolve session');
      }

      const payload = (await response.json()) as ServerSessionResponse;
      const nextUser = payload.user ?? null;
      const nextProfile = normalizeProfile(payload.profile ?? null);

      // A newer optimistic session won the race — do not overwrite it with a stale null.
      if (epochAtStart !== sessionEpochRef.current) {
        return nextUser ? { user: nextUser, profile: nextProfile } : null;
      }

      setUser(nextUser);
      setUserProfile(nextProfile);

      if (!nextUser) {
        return null;
      }

      return { user: nextUser, profile: nextProfile };
    } catch {
      if (epochAtStart !== sessionEpochRef.current) {
        return null;
      }
      setUser(null);
      setUserProfile(null);
      return null;
    }
  }, []);

  /** Read /api/auth/me without clearing an optimistic session on failure. */
  const peekServerSession = useCallback(async (): Promise<ServerSessionResponse | null> => {
    try {
      const response = await fetch('/api/auth/me', {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) {
        return null;
      }

      const payload = (await response.json()) as ServerSessionResponse;
      const nextUser = payload.user ?? null;
      const nextProfile = normalizeProfile(payload.profile ?? null);
      if (!nextUser) {
        return null;
      }

      setUser(nextUser);
      setUserProfile(nextProfile);
      return { user: nextUser, profile: nextProfile };
    } catch {
      return null;
    }
  }, []);

  const confirmServerSession = useCallback(async (): Promise<ServerSessionResponse | null> => {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const session = await peekServerSession();
      if (session?.user) {
        return session;
      }
      await new Promise(resolve => {
        setTimeout(resolve, 40 * (attempt + 1));
      });
    }
    return null;
  }, [peekServerSession]);

  useEffect(() => {
    let isMounted = true;

    const loadSession = async () => {
      try {
        // Server cookie session only — keep supabase-js off the marketing-page main thread.
        await fetchServerSession();
      } catch {
        if (isMounted) {
          setUser(null);
          setUserProfile(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadSession();

    return () => {
      isMounted = false;
    };
  }, [fetchServerSession]);

  const signIn = async (email: string, password: string) => {
    try {
      let recaptchaToken: string | null = null;
      try {
        const { executeRecaptchaEnterprise } = await import('@/lib/security/recaptcha-client');
        const { RECAPTCHA_ACTIONS } = await import('@/lib/security/recaptcha-actions');
        recaptchaToken = await executeRecaptchaEnterprise(RECAPTCHA_ACTIONS.LOGIN);
      } catch {
        // Server may soft-skip when Enterprise is unconfigured (local / placeholder keys).
        recaptchaToken = null;
      }

      const response = await fetch('/api/auth/signin', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          ...(recaptchaToken ? { recaptchaToken } : {}),
        }),
      });

      const result = (await response.json().catch(() => null)) as {
        error?: { message?: string } | string;
        user?: User | null;
        profile?: Partial<UserProfile> | null;
      } | null;

      if (!response.ok) {
        const message =
          typeof result?.error === 'string'
            ? result.error
            : result?.error?.message || 'Sign in failed. Please try again.';
        return { error: { message, status: response.status } as AuthError };
      }

      const responseProfile = normalizeProfile(result?.profile ?? null);
      if (result?.user && responseProfile) {
        // Optimistic session so role-based redirect can run; epoch blocks in-flight /me clears.
        sessionEpochRef.current += 1;
        setUser(result.user);
        setUserProfile(responseProfile);
        setLoading(false);

        // Hard navigations (and proxy) need the httpOnly cookie — confirm before returning.
        const confirmed = await confirmServerSession();
        if (!confirmed?.user) {
          sessionEpochRef.current += 1;
          setUser(null);
          setUserProfile(null);
          return {
            error: {
              message:
                'Signed in, but your session cookie could not be verified. Please try again.',
            } as AuthError,
          };
        }

        return {
          error: null,
          profile: confirmed.profile ?? responseProfile,
        };
      }

      const session = await confirmServerSession();
      if (!session?.user) {
        return {
          error: {
            message:
              'Signed in, but your session could not be established. Please refresh and try again.',
          } as AuthError,
        };
      }

      setLoading(false);
      return { error: null, profile: session.profile };
    } catch {
      return {
        error: {
          message: 'Unable to sign in right now. Please try again.',
        } as AuthError,
      };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    role: 'user' | 'provider' = 'user',
  ) => {
    const supabase = await getBrowserSupabase();
    if (!supabase) {
      return {
        error: {
          message: 'Supabase not configured. Please set up your environment variables.',
        } as AuthError,
      };
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role,
        },
      },
    });
    return { error: error as AuthError | null };
  };

  const signUpWithPhone = async (
    phone: string,
    fullName: string,
    role: 'user' | 'provider' = 'user',
  ) => {
    const supabase = await getBrowserSupabase();
    if (!supabase) {
      return {
        error: {
          message: 'Supabase not configured. Please set up your environment variables.',
        } as AuthError,
      };
    }

    const { error } = await supabase.auth.signInWithOtp({
      phone,
      options: {
        data: {
          full_name: fullName,
          role,
        },
      },
    });
    return { error: error as AuthError | null };
  };

  const signInWithPhone = async (phone: string) => {
    try {
      let recaptchaToken: string | null = null;
      try {
        const { executeRecaptchaEnterprise } = await import('@/lib/security/recaptcha-client');
        const { RECAPTCHA_ACTIONS } = await import('@/lib/security/recaptcha-actions');
        recaptchaToken = await executeRecaptchaEnterprise(RECAPTCHA_ACTIONS.PHONE_OTP);
      } catch {
        recaptchaToken = null;
      }

      const response = await fetch('/api/auth/phone/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          ...(recaptchaToken ? { recaptchaToken } : {}),
        }),
      });

      const result = (await response.json().catch(() => null)) as {
        error?: string;
        assessmentId?: string | null;
      } | null;
      if (!response.ok) {
        return {
          error: {
            message: result?.error || 'Failed to send code. Please try again.',
            status: response.status,
          } as AuthError,
        };
      }

      return { error: null, assessmentId: result?.assessmentId ?? null };
    } catch {
      return {
        error: {
          message: 'Failed to send code. Please try again.',
        } as AuthError,
      };
    }
  };

  const sendEmailOtp = async (params: {
    email: string;
    fullName?: string;
    role?: 'user' | 'customer' | 'provider' | 'admin';
    shouldCreateUser?: boolean;
  }) => {
    try {
      const response = await fetch('/api/auth/email/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      const result = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        return {
          error: {
            message: result?.error || 'Failed to send code. Please try again.',
            status: response.status,
          } as AuthError,
        };
      }

      return { error: null };
    } catch {
      return {
        error: {
          message: 'Failed to send code. Please try again.',
        } as AuthError,
      };
    }
  };

  const signInWithOAuth = async (provider: 'google') => {
    try {
      // @supabase/ssr browser client stores the PKCE verifier in cookies for
      // /api/auth/callback. skipBrowserRedirect so we can confirm the cookie
      // landed before navigating to Google.
      const { createClient } = await import('@/utils/supabase/client');
      const supabase = createClient();

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: buildOAuthCallbackUrl(),
          skipBrowserRedirect: true,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      });

      if (error) {
        return { error: error as AuthError };
      }

      if (!data?.url) {
        return {
          error: {
            message:
              'Google sign-in is not available. Enable the Google provider in Supabase Auth and configure a valid OAuth client.',
          } as AuthError,
        };
      }

      if (!hasPkceCodeVerifierCookie()) {
        return {
          error: {
            message:
              'Could not start Google sign-in (missing PKCE cookie). Allow cookies for this site and try again.',
          } as AuthError,
        };
      }

      window.location.assign(data.url);
      return { error: null };
    } catch (error) {
      return {
        error: {
          message:
            error instanceof Error ? error.message : 'Google sign-in failed. Please try again.',
        } as AuthError,
      };
    }
  };

  const verifyOtp = async (phone: string, token: string, assessmentId?: string | null) => {
    try {
      const response = await fetch('/api/auth/phone/verify', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          token,
          ...(assessmentId ? { assessmentId } : {}),
        }),
      });

      const result = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        return {
          error: {
            message: result?.error || 'Invalid verification code',
            status: response.status,
          } as AuthError,
        };
      }

      const session = await fetchServerSession();
      if (!session?.user) {
        return {
          error: {
            message:
              'Verified, but your session could not be established. Please refresh and try again.',
          } as AuthError,
        };
      }

      return { error: null };
    } catch {
      return {
        error: {
          message: 'Unable to verify the code right now. Please try again.',
        } as AuthError,
      };
    }
  };

  const verifyEmailOtp = async (email: string, token: string) => {
    try {
      const response = await fetch('/api/auth/email/verify', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token }),
      });

      const result = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        return {
          error: {
            message: result?.error || 'Invalid verification code',
            status: response.status,
          } as AuthError,
        };
      }

      const session = await fetchServerSession();
      if (!session?.user) {
        return {
          error: {
            message:
              'Verified, but your session could not be established. Please refresh and try again.',
          } as AuthError,
        };
      }

      return { error: null };
    } catch {
      return {
        error: {
          message: 'Unable to verify the code right now. Please try again.',
        } as AuthError,
      };
    }
  };

  const signOut = async () => {
    sessionEpochRef.current += 1;
    setUser(null);
    setUserProfile(null);

    if (typeof window !== 'undefined') {
      sessionStorage.clear();
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase') || key.includes('auth')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem('supabase.auth.token');
    }

    try {
      const supabase = await getBrowserSupabase();
      if (supabase) {
        await supabase.auth.signOut({ scope: 'local' });
      }
    } catch {
      // Ignore client-session cleanup failures and continue clearing the server session.
    }

    try {
      await clearServerSession();
    } catch {
      // Ignore server-session cleanup failures after local state is already cleared.
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    const supabase = await getBrowserSupabase();
    if (!supabase) {
      return {
        error: {
          message: 'Supabase not configured. Please set up your environment variables.',
        } as AuthError,
      };
    }

    if (!user) {
      return { error: { message: 'No user logged in' } as AuthError };
    }

    const { error } = await supabase.from('users').update(updates).eq('id', user.id);

    if (!error) {
      setUserProfile(prev => {
        if (!prev) {
          return null;
        }

        const nextProfile = { ...prev, ...updates } as UserProfile;
        if (updates.role) {
          nextProfile.role = normalizeUserRole(updates.role as RawUserRole);
        }

        return nextProfile;
      });
    }

    return { error: error as AuthError | null };
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signInWithPhone,
    sendEmailOtp,
    signInWithOAuth,
    verifyOtp,
    verifyEmailOtp,
    signUp,
    signUpWithPhone,
    signOut,
    updateProfile,
    refreshSession: async () => {
      await fetchServerSession();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Null on marketing routes, where `AuthProvider` is intentionally not mounted. */
export function useOptionalAuth() {
  return useContext(AuthContext) ?? null;
}
